import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useQueryState } from 'hooks/useQueryState';

import { useMutationPasswordReset } from 'api/mutations/passwordReset.mutation';
import { useQueryPasswordPatternWithToken } from 'api/queries/passwordPatternWithToken.query';
import { useQueryServerTime } from 'api/queries/serverTime.query';

import { PasswordInput } from 'components/common/PasswordInput/PasswordInput';

import { encrypt } from 'components/App/AuthPublicPages/NewDesign/Login/Login.utils';
import classes from 'components/App/AuthPublicPages/NewDesign/ResetPassword/ResetPassword.module.scss';
import { Layout } from 'components/App/AuthPublicPages/components/Layout/Layout';
import Button from 'components/DesignSystem/Button/Button';
import { CustomizedFormErrorMessage } from 'components/common';

interface IResetPasswordForm {
    password: string;
    token: string;
    passwordConfirmation: string;
}

export const ResetPassword = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const { handleSnackBar } = useCustomSnackBar();
    const history = useHistory();
    const { data: serverTime } = useQueryServerTime();
    const [mail] = useQueryState('mail');
    const [token] = useQueryState('token');
    const [mode] = useQueryState('mode');
    const { data: passwordPatternData, error: patternFetchError } = useQueryPasswordPatternWithToken({
        email: mail as string | undefined,
        token: token as string | undefined,
    });

    const { mutate } = useMutationPasswordReset();

    const form = useForm<IResetPasswordForm>({
        defaultValues: {
            password: '',
            passwordConfirmation: '',
        },
    });

    useEffect(() => {
        if (!mail && !token) {
            history.push('/login');
        }
    }, [mail, token]);

    const handleSaveUseForm = (value) => {
        setLoading(true);
        const params = {
            password: value.password,
            token: token,
        };

        const time = serverTime?.time as number;
        const payload = time !== 0 ? { encrypt: encrypt(JSON.stringify(params), time), time } : params;

        mutate(payload, {
            onSuccess: () => {
                handleSnackBar('Password was successfully changed', 'success');
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
            onSettled: () => {
                setLoading(true);
                history.push('/login');
            },
        });
    };

    const disableCopyPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        return false;
    };

    return (
        <Layout>
            <div className={classes.container}>
                <div className={classes.title}>{mode === 'reset' ? 'Reset' : 'Set'} your password</div>

                <div className={classes.subTitle}>
                    Password pattern{' '}
                    {passwordPatternData
                        ? passwordPatternData.message
                        : 'must contain at least one number and one uppercase and lowercase letter, and at least 6 or more characters'}
                </div>

                <form onSubmit={form.handleSubmit(handleSaveUseForm)} autoComplete={'off'}>
                    <div className={classes.formRows}>
                        <div className={classes.row}>
                            <span className={classes.label}>New Password</span>
                            <Controller
                                render={({ field }) => (
                                    <PasswordInput
                                        className={classes.loginInput}
                                        {...field}
                                        placeholder={'Enter New Password'}
                                        onClick={() => form.clearErrors(['password', 'passwordConfirmation'])}
                                        error={Boolean(form.formState.errors.password)}
                                    />
                                )}
                                name={'password'}
                                rules={{
                                    required: 'No password provided.',
                                    pattern: {
                                        value: new RegExp(passwordPatternData?.pattern || ''),
                                        message: 'Password does not meet requirements',
                                    },
                                }}
                                control={form.control}
                            />
                            {form.formState.errors.password && (
                                <CustomizedFormErrorMessage text={form.formState.errors.password.message} />
                            )}
                        </div>
                        <div className={classes.row}>
                            <span className={classes.label}>Confirm Password</span>
                            <Controller
                                render={({ field }) => (
                                    <PasswordInput
                                        className={classes.loginInput}
                                        {...field}
                                        placeholder={'Enter Confirm Password'}
                                        onClick={() => form.clearErrors(['password', 'passwordConfirmation'])}
                                        onPaste={disableCopyPaste}
                                        onCopy={disableCopyPaste}
                                        error={
                                            Boolean(form.formState.errors.passwordConfirmation) ||
                                            Boolean(patternFetchError)
                                        }
                                    />
                                )}
                                name={'passwordConfirmation'}
                                rules={{
                                    required: 'No Password Confirmation provided.',
                                    validate: (value) =>
                                        value === form.watch('password') ||
                                        'New Password and Confirm password do not match',
                                }}
                                control={form.control}
                            />
                            {form.formState.errors.passwordConfirmation && (
                                <CustomizedFormErrorMessage text={form.formState.errors.passwordConfirmation.message} />
                            )}
                            {patternFetchError && (
                                <CustomizedFormErrorMessage
                                    text={
                                        <>
                                            <span>Password reset link has expired. Please contact </span>
                                            <a href={'mailto:cs@foresight.works'}> support.</a>
                                        </>
                                    }
                                />
                            )}
                        </div>

                        <div className={classes.row}>
                            <Button variant={'primary'} disabled={loading} isLoading={loading} type={'submit'}>
                                {mode === 'reset' ? 'Reset' : 'Set'}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </Layout>
    );
};
